import React, { useState } from 'react';
import './featureTable.css';
import { getCleanupLookup, getMultiCleanupLookup } from '../../phonemes/cleanupIpa';
import { hasToneFeature, ipaToFeatures, IpaType, isStress, primaryStress } from '../../phonemes/ipa';
import { PhonemeList, segmentPhonemes } from '../../phonemes/segmentPhonemes';
import { getUnicodeStringMulti } from './FeaturesForIpa';
import { getAlternates } from '../../phonemes/getAlternates';
import { getPhthongAlternates } from '../../phonemes/getPossiblePhthongs';
import { getPossibleCombos } from '../../phonemes/getPossibleCombos';
import { ChoiceList } from '../widgets/ChoiceList';
import { IpaKeyboard } from '../widgets/IpaKeyboard';

interface ReplaceSymbolsProps {
  /** which replacements to offer */
  readonly replacements: ReplaceType[];
  /** segments to examine for potential replacements */
  readonly segments: PhonemeList;
  /** tell listener to replace old w/ use */
  readonly replace?: (old: string, use: string) => void;
  /** ask listener if there's already a rule replacing from with to */
  readonly hasRule?: (from: string, to: string) => boolean;
  /** ask listener what 'from' is converted to, if anything */
  readonly getTo?: (from: string) => string | null;

  /** syllable to stress: 0 (none), 1, 2, -2, -1 */
  readonly stress?: number;
  /** tell listener to add stress to the given syllable */
  readonly addStress?: (syllable: number) => void;
}

/** type of replacement: unknown IPA, make diphthongs, combine consonants, add missing stress */
type ReplaceType = 'unknown' | 'thongs' | 'combine' | 'stress';

/** display info about symbols that should possibly be replaced: unknown symbols & possible diphthongs */
export function ReplaceSymbols(props: ReplaceSymbolsProps) {
  const [lookup] = useState(() => getCleanupLookup());
  const [multiLookup] = useState(() => getMultiCleanupLookup());

  const unknown = props.replacements.includes('unknown') ? UnknownSymbols(props, lookup, multiLookup) : <></>;
  const thongs = props.replacements.includes('thongs') ? PhthongSymbols(props) : <></>;
  const combos = props.replacements.includes('combine') ? ComboSymbols(props) : <></>;
  const stress = props.replacements.includes('stress') ? AddStress(props) : <></>;
  if (unknown === null && thongs === null && combos === null && stress === null) {
    return <></>;
  }
  return (<>
    {unknown}
    {thongs}
    {combos}
    {stress}
  </>);
}

/** area that lists all unknown symbols & lets you replace them */
function UnknownSymbols(props: ReplaceSymbolsProps, lookup: Record<string, string>, multiLookup: Record<string, string>) {
  if (!props.segments.hasUnknown && !props.segments.phonemes.some(p => p.phoneme === '~')) {
    return null;
  }

  const alts = getAlternates(props.segments.phonemes, lookup, multiLookup);
  // use whatever replacement the existing rules already say
  const all = alts.map(a => [a[0], (props.getTo && props.getTo(a[0])) ?? a[1]]);
  return (<>
    <br />
    <div className='smallTitle'>Unhandled symbols</div>
    <table>
      <thead>
        <tr>
          <th>unknown</th>
          <th>unicode</th>
          <th>alternate</th>
          <th>features</th>
        </tr>
      </thead>
      <tbody>
        {all.map(sa => <SymbolRow symbol={sa[0]} alt={sa[1]} replace={props.replace} hasRule={props.hasRule} unicode={true} features={true} key={sa[0]} />)}
      </tbody>
    </table>
  </>);
}

/** area that lists all possible diphthongs & let's you replace them */
function PhthongSymbols(props: ReplaceSymbolsProps) {
  const thongs = getPhthongAlternates(props.segments.phonemes);
  if (thongs.length === 0) {
    return null;
  }

  return (<>
    <br />
    <div className='smallTitle'>Possible diphthongs</div>
    <table>
      <thead>
        <tr>
          <th>vowel pair</th>
          <th>alternate</th>
        </tr>
      </thead>
      <tbody>
        {thongs.map(sa => <SymbolRow symbol={sa[0]} alt={sa[1]} replace={props.replace} hasRule={props.hasRule} key={sa[0]} />)}
      </tbody>
    </table>
  </>);
}

/** area that lists possible combos (which could be connected by a combiner) & lets' you replace them */
function ComboSymbols(props: ReplaceSymbolsProps) {
  const combos = getPossibleCombos(props.segments.phonemes);
  if (combos.length === 0) {
    return null;
  }

  return (<>
    <br />
    <div className='smallTitle'>Possible combos (affricates, co-articulated consonants, clicks)</div>
    <table>
      <thead>
        <tr>
          <th>symbols</th>
          <th>alternate</th>
          <th>features</th>
        </tr>
      </thead>
      <tbody>
        {combos.map(sa => <SymbolRow symbol={sa[0]} alt={sa[1]} replace={props.replace} hasRule={props.hasRule} features={true} key={sa[0]} />)}
      </tbody>
    </table>
  </>);
}

interface SymbolRowProps {
  readonly symbol: string;
  readonly alt: string;
  readonly unicode?: true;
  readonly features?: true;
  readonly replace?: (old: string, use: string) => void;
  readonly hasRule?: (from: string, to: string) => boolean;
}

/** display info about a single, unknown symbol */
function SymbolRow(props: SymbolRowProps) {
  const [alt, setAlt] = useState(props.alt);
  const symbol = props.symbol;
  const features = getFeatureString(alt);

  const altInput = <IpaKeyboard cols={2} rows={1} value={alt} onChange={setAlt} useInput={true} />;

  const use = props.replace;
  const hideUse = features === undefined && alt !== '';
  const disable = props.hasRule && props.hasRule(symbol, alt);
  const text = props.hasRule ? 'change' : 'use';
  const useButton = use === undefined || hideUse ? <></>
    : <button onClick={() => use(symbol, alt)} disabled={disable}>{text}</button>

  return (
    <tr>
      <th>{symbol}</th>
      {props.unicode === true ? <td>{getUnicodeStringMulti(symbol)}</td> : <></>}
      <td className='alternate'>{altInput}{useButton}</td>
      {props.features === true ? <td className="features">{features === undefined ? <></> : features}</td> : <></>}
    </tr>
  );
}

/**
 * get a string describing all the features for /ipa/
 * e.g., /ˈy/ consists of independent segments,
 * which should return "prosody primary-stress, vowel close front rounded"
 */
function getFeatureString(ipa: IpaType): string {
  const segments = segmentPhonemes(ipa, 'ipa', 'all-aff');
  const pieces = segments.phonemes.map(p => p.phoneme);
  const featureList = pieces.map(p => {
    const list = ipaToFeatures(p);
    return list ? list.join(' ') : ''
  });
  return featureList.join(', ');
}

/** if there's no stress & no tones, suggest adding stress */
function AddStress(props: ReplaceSymbolsProps) {
  if (props.addStress === undefined || props.stress === undefined) {
    return null;
  }
  if (!needsStress(props)) {
    return null;
  }
  const stressStr = props.stress === 0 ? '' : props.stress.toString();

  function changeStress(s: string) {
    const n = s === '' ? 0 : Number.parseInt(s);
    if (props.addStress) {
      props.addStress(n);
    }
  }

  return (<>
    <br />
    <div className='smallTitle'>No stress or tone markers found</div>

    add stress to syllable:&nbsp;
    <ChoiceList items={['', '1', '2', '-2', '-1']} selectedItem={stressStr} onSelect={changeStress} />
  </>);
}

/** if there's no stress or tone markers & ' hasn't been converted to stress, we want to let them know it needs stress */
function needsStress(props: ReplaceSymbolsProps): boolean {
  const phones = props.segments.phonemes;
  if (phones.length === 0) {
    return false;
  }
  if (phones.some(p => isStress(p.phoneme) || hasToneFeature(p.phoneme))) {
    return false;
  }

  // check if there are single quotes that have been turned into stress markers
  const convertedStress = props.hasRule && props.hasRule("'", primaryStress)
    && phones.some(p => p.phoneme === "'");
  return !convertedStress;
}
